import { CmdModal, CmdModalFooterButton, CmdSuperSelect } from '@commander-services/gui-components';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQueries } from '@tanstack/react-query';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { IGroupChipSelectOptions } from '@commander-services/gui-components/build/components/CmdGroupChipSelectPerCustomer/CmdGroupChipSelectPerCustomer';
import PermissionsService, {
  PERMISSION_BLUECOINS,
  PERMISSION_RIDE_INFO_COMMENT_ASSIGNMENT,
  PERMISSION_RIDE_INFO_EVENT,
  PERMISSION_RIDE_INFO_TAGS_ASSIGMENT,
  PERMISSION_RIDE_INFO_WAYPOINT,
  PERMISSION_WAYPOINT_CUSTOMER,
  PERMISSION_WAYPOINT_PRIVATE,
} from '../../../Services/PermissionsService';
import { IVehicles } from '../../Vehicles/types';
import {
  ALARM_ACTIVE,
  ALARM_OFF,
  ALARM_WITHOUT,
  ANNOUNCEMENT_ALARM_KEY,
  ANNOUNCEMENT_BLUECOIN_KEY,
  ANNOUNCEMENT_EVENT_KEY,
  ANNOUNCEMENT_WAYPOINT_KEY,
  BLUECOIN_ACTIVE,
  BLUECOIN_OFF,
  BLUECOIN_WITHOUT,
  COMMENT_ACTIVE,
  COMMENT_OFF,
  DEFAULT_INFO_FILTER_FORM_VALUES,
  DEFAULT_INFO_FILTER_RADIO_BTN_VALUES,
  EVENT_ACTIVE,
  EVENT_OFF,
  EVENT_WITHOUT,
  ICheckedTableItems,
  ITripsOverviewAdvancedFilters,
  ITripsOverviewChipOption,
  ITripsOverviewTableData,
  PURPOSE_ACTIVE,
  PURPOSE_OFF,
  PURPOSE_WITHOUT,
  RIDEBOOK_PURPOSE_KEY,
  WP_ACTIVE,
  WP_OFF,
  WP_WITHOUT,
} from '../interfaces';
import { selectedVehiclesAtom, vehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  hasMoreAtom,
  isActiveAdvanceFilterAtom,
  offsetAtom,
  rideBookDataAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import {
  findChipName,
  findCustomerIdFromOptions,
  getCustomerIdFromSelectedVehicles,
  getDataForFilter,
  handleChipOptionsCategory,
  handleChipOptionsFixedCategory,
} from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IInforRideFilter {
  handleClose: () => void;
}

type TInfoRideFilterChoice =
  | 'waypointChoice'
  | 'eventChoice'
  | 'alarmChoice'
  | 'purposeChoice'
  | 'commentChoice'
  | 'bluecoinChoice';

export default function InforRideFilter(props: IInforRideFilter): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [advancedFilters, setAdvancedFilters] = useRecoilState(advancedFiltersAtom);
  const setIsActiveAdvanceFilter = useSetRecoilState(isActiveAdvanceFilterAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const selectedVehicles = useRecoilValue(selectedVehiclesAtom);
  const { trackEvent } = useAnalytics();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
    clearErrors,
  } = useForm<ITripsOverviewAdvancedFilters>({
    mode: 'onChange',
    defaultValues: {
      ...advancedFilters,
    },
  });

  const resetState = () => {
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
  };

  const handleCustomerIds = (): number[] => {
    const customerIds = getCustomerIdFromSelectedVehicles(vehicles, selectedVehicles);

    if (
      (watch('purposeChoice') === PURPOSE_ACTIVE ||
        watch('alarmChoice') === ALARM_ACTIVE ||
        watch('waypointChoice') === WP_ACTIVE) &&
      advancedFilters.customerId.length > 0
    ) {
      return [...new Set([...customerIds, ...advancedFilters.customerId])];
    }

    return customerIds;
  };

  const handleRadioButtons = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.currentTarget.id;
    const dataChoice = event.currentTarget.getAttribute('data-choice') as TInfoRideFilterChoice;

    if (dataChoice) {
      setValue(dataChoice, id);
      trackEvent(['ride book', 'click', `Ride book info for ride filter choice: ${id}`]);

      if (dataChoice === 'waypointChoice' && (id === WP_WITHOUT || id === WP_OFF)) {
        setValue('announcementWaypoint', []);
        clearErrors('announcementWaypoint');
      }
      if (dataChoice === 'eventChoice' && (id === EVENT_WITHOUT || id === EVENT_OFF)) {
        setValue('announcementEvent', []);
        clearErrors('announcementEvent');
      }
      if (dataChoice === 'alarmChoice' && (id === ALARM_WITHOUT || id === ALARM_OFF)) {
        setValue('announcementAlarm', []);
        clearErrors('announcementAlarm');
      }
      if (dataChoice === 'purposeChoice' && (id === PURPOSE_WITHOUT || id === PURPOSE_OFF)) {
        setValue('ridebookPurpose', {});
        clearErrors('ridebookPurpose');
      }
      if (dataChoice === 'bluecoinChoice' && (id === BLUECOIN_WITHOUT || id === BLUECOIN_OFF)) {
        setValue('announcementBluecoin', []);
        clearErrors('announcementBluecoin');
      }
    }
  };

  const handleFilters = async (filterName: string, isCustomerId = true) => {
    const customerIds = handleCustomerIds();

    let customerId: string | null = null;
    if (customerIds.length === 1) {
      customerId = String(customerIds[0]);
    } else if (customerIds.length > 1) {
      customerId = customerIds.join(',');
    }

    return (await getDataForFilter(filterName, isCustomerId ? customerId : null)) || false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [ANNOUNCEMENT_WAYPOINT_KEY, watch('waypointChoice')],
        queryFn: () => handleFilters(ANNOUNCEMENT_WAYPOINT_KEY),
        enabled: watch('waypointChoice') === WP_ACTIVE,
      },
      {
        queryKey: [ANNOUNCEMENT_EVENT_KEY, watch('eventChoice')],
        queryFn: () => handleFilters(ANNOUNCEMENT_EVENT_KEY),
        enabled: watch('eventChoice') === EVENT_ACTIVE,
      },
      {
        queryKey: [ANNOUNCEMENT_ALARM_KEY, watch('alarmChoice')],
        queryFn: () => handleFilters(ANNOUNCEMENT_ALARM_KEY, false),
        enabled: watch('alarmChoice') === ALARM_ACTIVE,
      },
      {
        queryKey: [RIDEBOOK_PURPOSE_KEY, watch('purposeChoice')],
        queryFn: () => handleFilters(RIDEBOOK_PURPOSE_KEY),
        enabled: watch('purposeChoice') === PURPOSE_ACTIVE,
      },
      {
        queryKey: [ANNOUNCEMENT_BLUECOIN_KEY, watch('bluecoinChoice')],
        queryFn: () => handleFilters(ANNOUNCEMENT_BLUECOIN_KEY, false),
        enabled: watch('bluecoinChoice') === BLUECOIN_ACTIVE,
      },
    ],
  });

  const { data: waypointData, isLoading: isLoadingWaypoint } = results[0];
  const { data: eventData, isLoading: isLoadingEvent } = results[1];
  const { data: alarmData, isLoading: isLoadingAlarm } = results[2];
  const { data: purposeData, isLoading: isLoadingPurpose } = results[3];
  const { data: bluecoinData, isLoading: isLoadingBluecoin } = results[4];

  const translateObjectKeys = (obj) => {
    return Object.keys(obj).reduce((translatedObj, key) => {
      const translatedKey = f({ id: key });
      translatedObj[translatedKey] = obj[key];
      return translatedObj;
    }, {});
  };

  const handleWaypointPermissions = (): boolean => {
    return (
      PermissionsService.hasAccess(PERMISSION_WAYPOINT_CUSTOMER, ['read'], handleCustomerIds()) ||
      PermissionsService.hasAccess(PERMISSION_WAYPOINT_PRIVATE, ['read'], handleCustomerIds())
    );
  };

  const handleResetButton = (): boolean => {
    if (
      watch('alarmChoice') !== ALARM_OFF ||
      watch('eventChoice') !== EVENT_OFF ||
      watch('waypointChoice') !== WP_OFF ||
      watch('purposeChoice') !== PURPOSE_OFF ||
      watch('commentChoice') !== COMMENT_OFF ||
      watch('bluecoinChoice') !== BLUECOIN_OFF
    ) {
      return true;
    }
    return false;
  };

  const handleResetForm = () => {
    reset(DEFAULT_INFO_FILTER_FORM_VALUES);
    handleResetButton();
    trackEvent(['ride book', 'click', 'Ride book reset info for ride filter']);
  };

  const onSubmit = (value: ITripsOverviewAdvancedFilters) => {
    if (Object.keys(errors).length > 0) {
      return;
    }
    trackEvent(['ride book', 'click', 'Ride book submit info for ride filter']);

    const customerIds = handleCustomerIds();

    let newCustomerIds: number[] = customerIds;

    if (watch('purposeChoice') === PURPOSE_ACTIVE && purposeData && purposeData?.values) {
      const selectedCustomerIds = findCustomerIdFromOptions(
        purposeData.values as ITripsOverviewChipOption[],
        value.ridebookPurpose
      );
      newCustomerIds = [...new Set([...selectedCustomerIds])];
    }
    if (watch('waypointChoice') === WP_ACTIVE && waypointData && waypointData?.values) {
      const selectedCustomerIds = findCustomerIdFromOptions(
        waypointData.values as ITripsOverviewChipOption[],
        value.announcementWaypoint
      );

      newCustomerIds = [...new Set([...selectedCustomerIds])];
    }
    if (watch('alarmChoice') === ALARM_ACTIVE && alarmData && alarmData?.values) {
      const selectedCustomerIds = findCustomerIdFromOptions(
        alarmData.values as ITripsOverviewChipOption[],
        value.announcementAlarm
      );
      newCustomerIds = [...new Set([...selectedCustomerIds])];
    }
    if (watch('bluecoinChoice') === BLUECOIN_ACTIVE && bluecoinData && bluecoinData?.values) {
      const selectedCustomerIds = findCustomerIdFromOptions(
        bluecoinData.values as ITripsOverviewChipOption[],
        value.announcementBluecoin
      );
      newCustomerIds = [...new Set([...selectedCustomerIds])];
    }

    setAdvancedFilters((oldState) => ({
      ...oldState,
      ...value,
      customerId: newCustomerIds,
      comment: value.commentChoice === COMMENT_ACTIVE,
    }));

    setIsActiveAdvanceFilter((oldState) => ({
      ...oldState,
      announcementWaypoint: value.waypointChoice !== WP_OFF,
      announcementEvent: value.eventChoice !== EVENT_OFF,
      announcementAlarm: value.alarmChoice !== ALARM_OFF,
      ridebookPurpose: value.purposeChoice !== PURPOSE_OFF,
      comment: value.commentChoice !== COMMENT_OFF,
      announcementBluecoin: value.bluecoinChoice !== BLUECOIN_OFF,
    }));

    resetState();
    props.handleClose();
  };

  const buttons: CmdModalFooterButton[] = handleResetButton()
    ? [
        {
          id: 'modalClose',
          type: 'button',
          title: 'Reset',
          className: 'e-button',
          callback: handleResetForm,
          tooltip: f({ id: 'tables.tripsOverview.modal.dayTime.resetBtn.title' }),
        },
        {
          id: 'submitEditModal',
          type: 'submit',
          title: f({ id: 'table.apply' }),
          className: 'e-button e-button--gray',
          submitCallback: handleSubmit(onSubmit),
          tooltip: f({ id: 'tables.tripsOverview.modal.dayTime.applyBtn.title' }),
        },
      ]
    : [
        {
          id: 'submitEditModal',
          type: 'submit',
          title: f({ id: 'table.apply' }),
          className: 'e-button e-button--gray',
          submitCallback: handleSubmit(onSubmit),
          tooltip: f({ id: 'tables.tripsOverview.modal.dayTime.applyBtn.title' }),
        },
      ];

  const showLoader =
    isLoadingWaypoint || isLoadingEvent || isLoadingAlarm || isLoadingPurpose || isLoadingBluecoin;

  let errorMessage: string = '';
  if (errors && errors.ridebookPurpose) {
    errorMessage =
      typeof errors.ridebookPurpose?.message === 'string' ? errors.ridebookPurpose.message : '';
  }
  return (
    <CmdModal
      id="day-time-filter-ride-book-modal"
      title={f({ id: 'tables.tripsOverview.modal.infoFilter.title' })}
      footerButtons={buttons}
      loader={showLoader}
      width="640px"
      closeCallbackForCloseIcon={props.handleClose}
    >
      <div className="p-0" style={{ margin: '-24px 0' }}>
        <div className="form-group m-0">
          {PermissionsService.hasAccess(
            PERMISSION_RIDE_INFO_WAYPOINT,
            ['read'],
            handleCustomerIds()
          ) &&
            handleWaypointPermissions() && (
              <div className="align-items-center my-6">
                <label
                  className="d-block form-label text-nowrap pr-0"
                  data-cy="infoFilter-form-checkbox-wp-label"
                  title={f({ id: 'tables.tripsOverview.modal.infoFilter.wp.tooltip' })}
                >
                  {f({ id: 'tables.tripsOverview.modal.infoFilter.wp.title' })}
                </label>
                <div className="d-block">
                  <div className="row no-gutters">
                    {DEFAULT_INFO_FILTER_RADIO_BTN_VALUES.waypoint.map((wpKey) => (
                      <div className="col-6 col-sm-4 my-1 my-sm-0" key={wpKey.id}>
                        <div className="e-radio">
                          <label>
                            <input
                              id={wpKey.id}
                              type="radio"
                              value={wpKey.id}
                              checked={watch('waypointChoice') === wpKey.id}
                              onChange={handleRadioButtons}
                              data-choice="waypointChoice"
                              data-cy={`infoFilter-form-checkbox-${wpKey.id}`}
                              // disabled={!handleWaypointPermissions()}
                            />
                            <span>
                              {f({ id: `tables.tripsOverview.modal.infoFilter.${wpKey.id}` })}
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="d-block pt-2 ml-auto">
                  <Controller
                    name={ANNOUNCEMENT_WAYPOINT_KEY}
                    control={control}
                    rules={{
                      required:
                        watch('waypointChoice') !== WP_ACTIVE
                          ? undefined
                          : f({ id: 'tables.tripsOverview.modal.infoFilter.wp.validation' }),
                    }}
                    render={({ field }) => (
                      <div style={{ margin: '-8px' }}>
                        <CmdSuperSelect
                          {...field}
                          id={ANNOUNCEMENT_WAYPOINT_KEY}
                          options={
                            waypointData
                              ? (handleChipOptionsCategory(
                                  waypointData.values as ITripsOverviewChipOption[],
                                  watch('announcementWaypoint'),
                                  [
                                    f({ id: 'alarms.modal.rule.choice.select' }),
                                    f({ id: 'alarms.modal.rule.choice.not.select' }),
                                  ]
                                ) as IGroupChipSelectOptions)
                              : {}
                          }
                          searchPlaceholder={f({
                            id: 'alarms.modal.rule.waypoint.search.placeholder',
                          })}
                          selectButton={f({ id: 'table.apply' })}
                          resetButton="Reset"
                          placeholder={f({
                            id: 'alarms.modal.rule.conditions.select.placeholder.choose',
                          })}
                          onChangeCallback={(value) => {
                            setValue('announcementWaypoint', value as string[]);
                            setValue(
                              'waypointChipName',
                              findChipName(
                                (waypointData &&
                                  (waypointData.values as ITripsOverviewChipOption[])) ||
                                  [],
                                value as string[]
                              )
                            );
                            if (
                              watch('announcementWaypoint').length > 0 &&
                              errors.announcementWaypoint
                            ) {
                              clearErrors('announcementWaypoint');
                            }
                          }}
                          error={errors.announcementWaypoint?.message}
                          touched={true}
                          tooltip={f({
                            id: 'tables.tripsOverview.modal.infoFilter.wp.choose.tooltip',
                          })}
                          disabled={watch('waypointChoice') !== WP_ACTIVE}
                          isArrayValue
                          hasButton
                          textAfterDeletedItem={`(${f({
                            id: 'tables.tripsOverview.modal.infoFilter.archived.shortcut',
                          })})`}
                          disableTooltipOnChips
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            )}
          {PermissionsService.hasAccess(
            PERMISSION_RIDE_INFO_EVENT,
            ['read'],
            handleCustomerIds()
          ) && (
            <div className="align-items-center my-6">
              <label
                className="d-block form-label text-nowrap pr-0"
                data-cy="infoFilter-form-checkbox-event-label"
                title={f({ id: 'tables.tripsOverview.modal.infoFilter.event.tooltip' })}
              >
                {f({ id: 'tables.tripsOverview.modal.infoFilter.event.title' })}
              </label>
              <div className="d-block">
                <div className="row no-gutters">
                  {DEFAULT_INFO_FILTER_RADIO_BTN_VALUES.event.map((eventKey) => (
                    <div className="col-6 col-sm-4 my-1 my-sm-0" key={eventKey.id}>
                      <div className="e-radio">
                        <label>
                          <input
                            id={eventKey.id}
                            type="radio"
                            value={eventKey.id}
                            checked={watch('eventChoice') === eventKey.id}
                            onChange={handleRadioButtons}
                            data-choice="eventChoice"
                            data-cy={`infoFilter-form-checkbox-${eventKey.id}`}
                          />
                          <span>
                            {f({ id: `tables.tripsOverview.modal.infoFilter.${eventKey.id}` })}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="d-block pt-2 ml-auto">
                <Controller
                  name={ANNOUNCEMENT_EVENT_KEY}
                  control={control}
                  rules={{
                    required:
                      watch('eventChoice') !== EVENT_ACTIVE
                        ? undefined
                        : f({
                            id: 'tables.tripsOverview.modal.infoFilter.event.validation',
                          }),
                  }}
                  render={({ field }) => (
                    <div style={{ margin: '-8px' }}>
                      <CmdSuperSelect
                        {...field}
                        id={ANNOUNCEMENT_EVENT_KEY}
                        // name="announcementWaypoint"
                        options={
                          eventData
                            ? (translateObjectKeys(
                                handleChipOptionsFixedCategory(
                                  eventData.values as ITripsOverviewChipOption[]
                                )
                              ) as IGroupChipSelectOptions)
                            : {}
                        }
                        searchPlaceholder={f({
                          id: 'alarms.modal.rule.waypoint.search.placeholder',
                        })}
                        selectButton={f({ id: 'table.apply' })}
                        resetButton="Reset"
                        placeholder={f({
                          id: 'alarms.modal.rule.conditions.select.placeholder.choose',
                        })}
                        onChangeCallback={(value) => {
                          setValue('announcementEvent', value as string[]);
                          setValue(
                            'eventChipName',
                            findChipName(
                              (eventData && (eventData.values as ITripsOverviewChipOption[])) || [],
                              value as string[]
                            )
                          );
                          if (watch('announcementEvent').length > 0 && errors.announcementEvent) {
                            clearErrors('announcementEvent');
                          }
                        }}
                        error={errors.announcementEvent?.message}
                        touched={true}
                        tooltip={f({
                          id: 'tables.tripsOverview.modal.infoFilter.event.choose.tooltip',
                        })}
                        disabled={watch('eventChoice') !== EVENT_ACTIVE}
                        hasItemPadding={true}
                        isArrayValue
                        hasButton
                        textAfterDeletedItem={`(${f({
                          id: 'tables.tripsOverview.modal.infoFilter.archived.shortcut',
                        })})`}
                        disableTooltipOnChips
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          )}
          {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read'], handleCustomerIds()) && (
            <div className="align-items-center my-6">
              <label
                className="d-block form-label text-nowrap pr-0"
                data-cy="infoFilter-form-checkbox-bluecoin-label"
                title={f({ id: 'tables.tripsOverview.modal.infoFilter.bluecoin.tooltip' })}
              >
                {f({ id: 'tables.tripsOverview.modal.infoFilter.bluecoin.title' })}
              </label>
              <div className="d-block">
                <div className="row no-gutters">
                  {DEFAULT_INFO_FILTER_RADIO_BTN_VALUES.bluecoin.map((bluecoin) => (
                    <div className="col-6 col-sm-4 my-1 my-sm-0" key={bluecoin.id}>
                      <div className="e-radio">
                        <label>
                          <input
                            id={bluecoin.id}
                            type="radio"
                            value={bluecoin.id}
                            checked={watch('bluecoinChoice') === bluecoin.id}
                            onChange={handleRadioButtons}
                            data-choice="bluecoinChoice"
                            data-cy={`infoFilter-form-checkbox-${bluecoin.id}`}
                          />
                          <span>
                            {f({ id: `tables.tripsOverview.modal.infoFilter.${bluecoin.id}` })}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="d-block pt-2 ml-auto">
                <Controller
                  name={ANNOUNCEMENT_BLUECOIN_KEY}
                  control={control}
                  rules={{
                    required:
                      watch('bluecoinChoice') !== BLUECOIN_ACTIVE
                        ? undefined
                        : f({
                            id: 'tables.tripsOverview.modal.infoFilter.bluecoin.validation',
                          }),
                  }}
                  render={({ field }) => (
                    <div style={{ margin: '-8px' }}>
                      <CmdSuperSelect
                        {...field}
                        id={ANNOUNCEMENT_BLUECOIN_KEY}
                        options={
                          bluecoinData
                            ? (handleChipOptionsCategory(
                                bluecoinData.values as ITripsOverviewChipOption[],
                                watch('announcementBluecoin'),
                                [
                                  f({ id: 'alarms.modal.rule.choice.select' }),
                                  f({ id: 'alarms.modal.rule.choice.not.select' }),
                                ]
                              ) as IGroupChipSelectOptions)
                            : {}
                        }
                        searchPlaceholder={f({
                          id: 'alarms.modal.rule.waypoint.search.placeholder',
                        })}
                        selectButton={f({ id: 'table.apply' })}
                        resetButton="Reset"
                        placeholder={f({
                          id: 'alarms.modal.rule.conditions.select.placeholder.choose',
                        })}
                        onChangeCallback={(value) => {
                          setValue('announcementBluecoin', value as string[]);
                          setValue(
                            'bluecoinChipName',
                            findChipName(
                              (bluecoinData &&
                                (bluecoinData.values as ITripsOverviewChipOption[])) ||
                                [],
                              value as string[]
                            )
                          );
                          if (
                            watch('announcementBluecoin').length > 0 &&
                            errors.announcementBluecoin
                          ) {
                            clearErrors('announcementBluecoin');
                          }
                        }}
                        error={errors.announcementBluecoin?.message}
                        touched={true}
                        tooltip={f({
                          id: 'tables.tripsOverview.modal.infoFilter.bluecoin.choose.tooltip',
                        })}
                        disabled={watch('bluecoinChoice') !== BLUECOIN_ACTIVE}
                        isArrayValue
                        hasButton
                        textAfterDeletedItem={`(${f({
                          id: 'tables.tripsOverview.modal.infoFilter.archived.shortcut',
                        })})`}
                        disableTooltipOnChips
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          )}

          <div className="align-items-center my-6">
            <label
              className="d-block form-label text-nowrap pr-0"
              data-cy="infoFilter-form-checkbox-alarm-label"
              title={f({ id: 'tables.tripsOverview.modal.infoFilter.alarm.tooltip' })}
            >
              {f({ id: 'tables.tripsOverview.modal.infoFilter.alarm.title' })}
            </label>
            <div className="d-block">
              <div className="row no-gutters">
                {DEFAULT_INFO_FILTER_RADIO_BTN_VALUES.alarm.map((alarmKey) => (
                  <div className="col-6 col-sm-4 my-1 my-sm-0" key={alarmKey.id}>
                    <div className="e-radio">
                      <label>
                        <input
                          id={alarmKey.id}
                          type="radio"
                          value={alarmKey.id}
                          checked={watch('alarmChoice') === alarmKey.id}
                          onChange={handleRadioButtons}
                          data-choice="alarmChoice"
                          data-cy={`infoFilter-form-checkbox-${alarmKey.id}`}
                        />
                        <span>
                          {f({ id: `tables.tripsOverview.modal.infoFilter.${alarmKey.id}` })}
                        </span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="d-block pt-2 ml-auto">
              <Controller
                name={ANNOUNCEMENT_ALARM_KEY}
                control={control}
                rules={{
                  required:
                    watch('alarmChoice') !== ALARM_ACTIVE
                      ? undefined
                      : f({
                          id: 'tables.tripsOverview.modal.infoFilter.alarm.validation',
                        }),
                }}
                render={({ field }) => (
                  <div style={{ margin: '-8px' }}>
                    <CmdSuperSelect
                      {...field}
                      id={ANNOUNCEMENT_ALARM_KEY}
                      options={
                        alarmData
                          ? (handleChipOptionsCategory(
                              alarmData.values as ITripsOverviewChipOption[],
                              watch('announcementAlarm'),
                              [
                                f({ id: 'alarms.modal.rule.choice.select' }),
                                f({ id: 'alarms.modal.rule.choice.not.select' }),
                              ]
                            ) as IGroupChipSelectOptions)
                          : {}
                      }
                      searchPlaceholder={f({
                        id: 'alarms.modal.rule.waypoint.search.placeholder',
                      })}
                      selectButton={f({ id: 'table.apply' })}
                      resetButton="Reset"
                      placeholder={f({
                        id: 'alarms.modal.rule.conditions.select.placeholder.choose',
                      })}
                      onChangeCallback={(value) => {
                        setValue('announcementAlarm', value as string[]);
                        setValue(
                          'alarmChipName',
                          findChipName(
                            (alarmData && (alarmData.values as ITripsOverviewChipOption[])) || [],
                            value as string[]
                          )
                        );
                        if (watch('announcementAlarm').length > 0 && errors.announcementAlarm) {
                          clearErrors('announcementAlarm');
                        }
                      }}
                      error={errors.announcementAlarm?.message}
                      touched={true}
                      tooltip={f({
                        id: 'tables.tripsOverview.modal.infoFilter.alarm.choose.tooltip',
                      })}
                      disabled={watch('alarmChoice') !== ALARM_ACTIVE}
                      isArrayValue
                      hasButton
                      textAfterDeletedItem={`(${f({
                        id: 'tables.tripsOverview.modal.infoFilter.archived.shortcut',
                      })})`}
                      disableTooltipOnChips
                    />
                  </div>
                )}
              />
            </div>
          </div>
          {PermissionsService.hasAccess(
            PERMISSION_RIDE_INFO_TAGS_ASSIGMENT,
            ['read'],
            handleCustomerIds()
          ) && (
            <div className="align-items-center my-6">
              <label
                className="d-block form-label text-nowrap pr-0"
                data-cy="infoFilter-form-checkbox-purpose-label"
                title={f({ id: 'tables.tripsOverview.modal.infoFilter.purpose.tooltip' })}
              >
                {f({ id: 'tables.tripsOverview.modal.infoFilter.purpose.title' })}
              </label>
              <div className="d-block">
                <div className="row no-gutters">
                  {DEFAULT_INFO_FILTER_RADIO_BTN_VALUES.purpose.map((purposeKey) => (
                    <div className="col-6 col-sm-4 my-1 my-sm-0" key={purposeKey.id}>
                      <div className="e-radio">
                        <label>
                          <input
                            id={purposeKey.id}
                            type="radio"
                            value={purposeKey.id}
                            checked={watch('purposeChoice') === purposeKey.id}
                            onChange={handleRadioButtons}
                            data-choice="purposeChoice"
                            data-cy={`infoFilter-form-checkbox-${purposeKey.id}`}
                          />
                          <span>
                            {f({ id: `tables.tripsOverview.modal.infoFilter.${purposeKey.id}` })}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="d-block pt-2 ml-auto">
                <Controller
                  name={RIDEBOOK_PURPOSE_KEY}
                  control={control}
                  rules={{
                    validate: (value) =>
                      watch('purposeChoice') !== PURPOSE_ACTIVE
                        ? undefined
                        : Object.keys(value).length !== 0 ||
                          f({ id: 'tables.tripsOverview.modal.infoFilter.purpose.validation' }),
                  }}
                  render={({ field }) => (
                    <div style={{ margin: '-8px' }}>
                      <CmdSuperSelect
                        {...field}
                        id={RIDEBOOK_PURPOSE_KEY}
                        options={
                          purposeData
                            ? (handleChipOptionsCategory(
                                purposeData.values as ITripsOverviewChipOption[],
                                watch('ridebookPurpose'),
                                [
                                  f({ id: 'alarms.modal.rule.choice.select' }),
                                  f({ id: 'alarms.modal.rule.choice.not.select' }),
                                ]
                              ) as IGroupChipSelectOptions)
                            : {}
                        }
                        searchPlaceholder={f({
                          id: 'alarms.modal.rule.waypoint.search.placeholder',
                        })}
                        selectButton={f({ id: 'table.apply' })}
                        resetButton="Reset"
                        placeholder={f({
                          id: 'alarms.modal.rule.conditions.select.placeholder.choose',
                        })}
                        // errorTooltip="Konecna zmazane data"
                        onChangeCallback={(
                          value: { [customerId: string]: string[] } | string[]
                        ) => {
                          setValue('ridebookPurpose', value as { [customerId: string]: string[] });

                          if (Object.values(value).length > 0) {
                            setValue(
                              'purposeChipName',
                              findChipName(
                                (purposeData &&
                                  (purposeData.values as ITripsOverviewChipOption[])) ||
                                  [],
                                value
                              )
                            );
                          }
                          if (
                            Object.values(watch('ridebookPurpose')).length > 0 &&
                            errors.ridebookPurpose
                          ) {
                            clearErrors('ridebookPurpose');
                          }
                        }}
                        error={errorMessage}
                        touched={true}
                        tooltip={f({
                          id: 'tables.tripsOverview.modal.infoFilter.purpose.choose.tooltip',
                        })}
                        disabled={watch('purposeChoice') !== PURPOSE_ACTIVE}
                        hasButton
                        textAfterDeletedItem={`(${f({
                          id: 'tables.tripsOverview.modal.infoFilter.archived.shortcut',
                        })})`}
                        disableTooltipOnChips
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          )}
          {PermissionsService.hasAccess(
            PERMISSION_RIDE_INFO_COMMENT_ASSIGNMENT,
            ['read'],
            handleCustomerIds()
          ) && (
            <div className="align-items-center my-6">
              <label
                className="d-block form-label text-nowrap pr-0"
                data-cy="infoFilter-form-checkbox-comment-label"
                title={f({ id: 'tables.tripsOverview.modal.infoFilter.comment.tooltip' })}
              >
                {f({ id: 'tables.tripsOverview.modal.infoFilter.comment.title' })}
              </label>
              <div className="d-block">
                <div className="row no-gutters">
                  {DEFAULT_INFO_FILTER_RADIO_BTN_VALUES.comment.map((commentKey) => (
                    <div className="col-6 col-sm-4 my-1 my-sm-0" key={commentKey.id}>
                      <div className="e-radio">
                        <label>
                          <input
                            id={commentKey.id}
                            type="radio"
                            value={commentKey.id}
                            checked={watch('commentChoice') === commentKey.id}
                            onChange={handleRadioButtons}
                            data-choice="commentChoice"
                            data-cy={`infoFilter-form-checkbox-${commentKey.id}`}
                          />
                          <span>
                            {f({ id: `tables.tripsOverview.modal.infoFilter.${commentKey.id}` })}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="d-block pt-2 ml-auto" />
            </div>
          )}
        </div>
      </div>
    </CmdModal>
  );
}
